"use client";

import { useState } from "react";
import { Ambulance, Lock, Mail, Eye, EyeOff, ShieldCheck, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ROLE_LABELS, ROLE_BADGE_COLORS, type DemoUser, TENANT_USERS } from "@/lib/auth/permissions";
import { AuthProvider, useAuth } from "@/lib/auth/auth-context";
import { useTenant } from "@/hooks/useTenant";
import { cn } from "@/lib/utils";

function LoginForm() {
  const router = useRouter();
  const { login } = useAuth();
  const tenant = useTenant();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { error } = await login(email, password);
      if (error) {
        setError(tenant.showDemoShortcuts ? `${error} Use a demo account below.` : error);
        return;
      }
      router.push("/dashboard");
    } catch {
      setError("Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDemoLogin(user: DemoUser) {
    setLoading(true);
    const { error } = await login(user.email, user.password);
    setLoading(false);
    if (!error) router.push("/dashboard");
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 text-white border-r border-white/5">
        <div className="flex items-center gap-3">
          {tenant.logo ? (
            <img src={tenant.logo} alt={tenant.orgName} className="h-11 w-11 rounded-xl object-cover shadow-lg" />
          ) : (
            <div className={cn(
              "flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg",
              tenant.accentFrom, tenant.accentTo, tenant.accentShadow
            )}>
              <Ambulance className="h-6 w-6 text-white" />
            </div>
          )}
          <div>
            <p className="text-base font-extrabold text-white tracking-tight">{tenant.orgName}</p>
            <p className="text-[11px] text-slate-500">{tenant.tagline}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="text-4xl font-bold leading-tight text-white">
              Gold-standard care,<br />
              <span className="text-blue-400">seamless management.</span>
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              A fully integrated platform for ambulance and primary care services — built for frontline teams, designed to exceed CQC standards.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "CQC Compliant", icon: ShieldCheck, desc: "All 5 key standards" },
              { label: "GDPR Secure", icon: Lock, desc: "NHS data standards" },
            ].map((item) => (
              <div key={item.label} className="rounded-xl bg-white/5 border border-white/8 p-4 space-y-1.5">
                <item.icon className="h-5 w-5 text-blue-400" />
                <p className="font-semibold text-sm text-white">{item.label}</p>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-600">{tenant.orgName} · Powered by OpsCom</p>
      </div>

      {/* Right login panel */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-[420px] space-y-5">

          {/* Mobile logo */}
          <div className="flex items-center gap-3 lg:hidden mb-2">
            <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br", tenant.accentFrom, tenant.accentTo)}>
              <Ambulance className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-extrabold text-white">{tenant.orgName}</span>
          </div>

          {/* Form card */}
          <div className="rounded-2xl bg-white shadow-2xl shadow-black/40 p-7 space-y-5">
            <div>
              <h2 className="text-[22px] font-bold text-slate-900 leading-tight">Sign in</h2>
              <p className="text-sm text-slate-500 mt-0.5">Access your clinical management portal</p>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-xs font-medium text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-3.5">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 mt-3 h-4 w-4 text-slate-400 pointer-events-none z-10" />
                <Input
                  label="Email address"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  required
                  autoComplete="email"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 mt-3 h-4 w-4 text-slate-400 pointer-events-none z-10" />
                <Input
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-9"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 mt-3 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <div className="flex items-center justify-between pt-0.5">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" className="rounded border-slate-300 text-blue-600" />
                  Remember me
                </label>
                <Link href="#" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  Forgot password?
                </Link>
              </div>

              <Button type="submit" className="w-full" size="lg" isLoading={loading}>
                Sign In
              </Button>
            </form>

            {tenant.showDemoShortcuts && (
            <>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs text-slate-400 bg-white px-2">
                or sign in as a demo user
              </div>
            </div>

            {/* Demo accounts */}
            <div className="space-y-1.5">
              {(TENANT_USERS[tenant.id] ?? []).map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleDemoLogin(u)}
                  disabled={loading}
                  className="group w-full flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 hover:border-blue-300 hover:bg-blue-50/40 transition-all text-left"
                >
                  <div className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-gradient-to-br",
                    u.avatarColor
                  )}>
                    {u.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-800">{u.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{u.email}</p>
                  </div>
                  <span className={cn("text-[10px] font-bold rounded-md border px-1.5 py-0.5 flex-shrink-0", ROLE_BADGE_COLORS[u.role])}>
                    {ROLE_LABELS[u.role]}
                  </span>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
            </>
            )}
          </div>

          <p className="text-center text-[11px] text-slate-500">
            This system is for authorised users only. All access is monitored and logged.{" "}
            <Link href="#" className="text-slate-400 hover:text-white">Privacy Policy</Link>
            {" · "}
            <Link href="#" className="text-slate-400 hover:text-white">Terms of Use</Link>
          </p>

          <p className="text-center text-xs text-slate-500 mt-2">
            Are you a client?{" "}
            <Link href="/portal/login" className="text-blue-400 hover:text-blue-300 font-medium">
              Client Portal →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <AuthProvider>
      <LoginForm />
    </AuthProvider>
  );
}
